import React, { useState } from 'react';

// =====================================================================
// A small, safe renderer for PrePraxis web study guides (2026-10-28).
// Supports exactly what the guide writers are allowed to use:
//   ## / ### headings, paragraphs, **bold**, *italic*, bullet and numbered
//   lists, "- [ ]" checklists, pipe tables, "> " quotes, callouts
//   (:::example / :::errors / :::tip / :::key ... :::) and ```quiz blocks.
// Nothing is injected as raw HTML, so guide text can never run scripts.
// The same tree renders on screen (interactive quizzes) and in the print
// window used for "Download PDF" (answers listed after each quiz).
// =====================================================================

export type GuideBlock =
  | { t: 'h2' | 'h3' | 'p'; text: string }
  | { t: 'ul' | 'ol'; items: string[] }
  | { t: 'check'; items: string[] }
  | { t: 'quote'; lines: string[] }
  | { t: 'table'; head: string[]; rows: string[][] }
  | { t: 'callout'; kind: string; title: string; blocks: GuideBlock[] }
  | { t: 'quiz'; items: QuizItem[] };

export interface QuizItem {
  passage: string[];
  question: string;
  options: string[];
  answer: number; // 0 to 3, or -1 if missing
  why: string;
  whyLabel: string;
}

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

function parseQuiz(lines: string[]): QuizItem[] {
  const items: QuizItem[] = [];
  let cur: QuizItem | null = null;
  let pendingPassage: string[] = [];
  let mode: 'q' | 'why' | null = null;
  const start = () => {
    cur = { passage: pendingPassage, question: '', options: [], answer: -1, why: '', whyLabel: '' };
    pendingPassage = [];
    items.push(cur);
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) { mode = null; continue; }
    const c = cur as QuizItem | null;
    let m: RegExpMatchArray | null;
    if (/^>\s?/.test(line)) {
      // A passage line belongs to the next question unless we are still
      // inside a question that has no options yet.
      if (c && c.options.length === 0 && c.question === '') c.passage.push(line.replace(/^>\s?/, ''));
      else pendingPassage.push(line.replace(/^>\s?/, ''));
      continue;
    }
    if ((m = line.match(/^Q[:.]\s*(.*)$/))) { start(); (cur as unknown as QuizItem).question = m[1]; mode = 'q'; continue; }
    if (c && (m = line.match(/^([A-E])[).]\s+(.*)$/))) { c.options.push(m[2]); mode = null; continue; }
    if (c && (m = line.match(/^Answer[:.]\s*([A-E])/i))) { c.answer = LETTERS.indexOf(m[1].toUpperCase()); mode = null; continue; }
    if (c && (m = line.match(/^Why(\s*\(([^)]*)\))?[:.]\s*(.*)$/i))) { c.whyLabel = m[2] || ''; c.why = m[3]; mode = 'why'; continue; }
    if (c && mode === 'why') { c.why += ' ' + line.trim(); continue; }
    if (c && mode === 'q' && c.options.length === 0) { c.question += ' ' + line.trim(); continue; }
  }
  return items.filter((q) => q.question && q.options.length >= 2);
}

// Questions may already carry their own number ("30. According to...").
function splitNumber(q: string, fallback: number): [number, string] {
  const m = q.match(/^\s*(\d{1,3})[.)]\s+(.*)$/);
  return m ? [Number(m[1]), m[2]] : [fallback, q];
}

export function parseGuideMarkdown(src: string): GuideBlock[] {
  const lines = String(src || '').replace(/\r\n/g, '\n').split('\n');
  return parseLines(lines);
}

function parseLines(lines: string[]): GuideBlock[] {
  const out: GuideBlock[] = [];
  let i = 0;
  const isSpecial = (l: string) =>
    /^#{2,3}\s/.test(l) || /^:::/.test(l) || /^```/.test(l) || /^\s*[-*]\s+/.test(l) || /^\s*\d+[.)]\s+/.test(l) || /^>\s?/.test(l) || /^\|/.test(l.trim());
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    let m: RegExpMatchArray | null;
    if ((m = line.match(/^```\s*quiz\s*$/i))) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) body.push(lines[i++]);
      i++;
      out.push({ t: 'quiz', items: parseQuiz(body) });
      continue;
    }
    if ((m = line.match(/^:::\s*([a-z]+)\s*(.*)$/i)) && m[1]) {
      const kind = m[1].toLowerCase();
      const title = m[2] || '';
      const body: string[] = [];
      i++;
      let depth = 0;
      while (i < lines.length) {
        if (/^:::\s*[a-z]+/i.test(lines[i])) depth++;
        else if (/^:::\s*$/.test(lines[i])) { if (depth === 0) break; depth--; }
        body.push(lines[i++]);
      }
      i++;
      out.push({ t: 'callout', kind, title, blocks: parseLines(body) });
      continue;
    }
    if ((m = line.match(/^(#{2,3})\s+(.*)$/))) {
      out.push({ t: m[1].length === 2 ? 'h2' : 'h3', text: m[2].trim() });
      i++;
      continue;
    }
    if (/^\|/.test(line.trim())) {
      const rows: string[][] = [];
      while (i < lines.length && /^\|/.test(lines[i].trim())) {
        const cells = lines[i].trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i++;
      }
      if (rows.length) out.push({ t: 'table', head: rows[0], rows: rows.slice(1) });
      continue;
    }
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) q.push(lines[i++].replace(/^>\s?/, ''));
      out.push({ t: 'quote', lines: q });
      continue;
    }
    if (/^\s*[-*]\s+\[[ xX]?\]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+\[[ xX]?\]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*]\s+\[[ xX]?\]\s+/, ''));
      out.push({ t: 'check', items });
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]) && !/^\s*[-*]\s+\[[ xX]?\]\s+/.test(lines[i])) {
        let item = lines[i++].replace(/^\s*[-*]\s+/, '');
        while (i < lines.length && lines[i].trim() && /^\s{2,}\S/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i])) item += ' ' + lines[i++].trim();
        items.push(item);
      }
      out.push({ t: 'ul', items });
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        let item = lines[i++].replace(/^\s*\d+[.)]\s+/, '');
        while (i < lines.length && lines[i].trim() && /^\s{2,}\S/.test(lines[i]) && !/^\s*\d+[.)]\s+/.test(lines[i])) item += ' ' + lines[i++].trim();
        items.push(item);
      }
      out.push({ t: 'ol', items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !isSpecial(lines[i])) para.push(lines[i++].trim());
    if (para.length) out.push({ t: 'p', text: para.join(' ') });
    else i++;
  }
  return out;
}

// **bold**, *italic* and `code`, nothing else.
export function Inline({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) parts.push(<code key={k++}>{tok.slice(1, -1)}</code>);
    else parts.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

const CALLOUT_LABEL: Record<string, string> = { example: 'Worked Example', errors: 'Common Errors', tip: 'Study Tip', key: 'Key Idea' };

function QuizView({ items, print, startNumber }: { items: QuizItem[]; print?: boolean; startNumber: number }) {
  const [picked, setPicked] = useState<Record<number, number>>({});
  if (print) {
    return (
      <div className="gm-quiz">
        {items.map((q, i) => (
          <div key={i} className="gm-q">
            {q.passage.length > 0 && <div className="gm-passage">{q.passage.map((l, j) => <p key={j}><Inline text={l} /></p>)}</div>}
            <p className="gm-qtext"><strong>{splitNumber(q.question, startNumber + i)[0]}.</strong> <Inline text={splitNumber(q.question, startNumber + i)[1]} /></p>
            <ol className="gm-opts" type="A">{q.options.map((o, j) => <li key={j}><Inline text={o} /></li>)}</ol>
          </div>
        ))}
        <div className="gm-answers">
          <p><strong>Answers</strong></p>
          {items.map((q, i) => (
            <p key={i}><strong>{splitNumber(q.question, startNumber + i)[0]}. {LETTERS[q.answer] || '?'}</strong>{q.whyLabel ? ` (${q.whyLabel})` : ''}. <Inline text={q.why} /></p>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="gm-quiz">
      {items.map((q, i) => {
        const p = picked[i];
        const done = p !== undefined;
        return (
          <div key={i} className="gm-q">
            {q.passage.length > 0 && <div className="gm-passage">{q.passage.map((l, j) => <p key={j}><Inline text={l} /></p>)}</div>}
            <p className="gm-qtext"><strong>{splitNumber(q.question, startNumber + i)[0]}.</strong> <Inline text={splitNumber(q.question, startNumber + i)[1]} /></p>
            <div className="gm-choices">
              {q.options.map((o, j) => {
                const state = !done ? '' : j === q.answer ? ' gm-right' : j === p ? ' gm-wrong' : ' gm-dim';
                return (
                  <button key={j} type="button" disabled={done} onClick={() => setPicked((s) => ({ ...s, [i]: j }))} className={`gm-choice${state}`}>
                    <span className="gm-letter">{LETTERS[j]}</span>
                    <span><Inline text={o} /></span>
                  </button>
                );
              })}
            </div>
            {done && (
              <div className={`gm-why ${p === q.answer ? 'gm-why-ok' : 'gm-why-no'}`}>
                <strong>{p === q.answer ? 'Correct.' : `Not quite. The answer is ${LETTERS[q.answer]}.`}</strong>{q.whyLabel ? <span className="gm-cat"> {q.whyLabel}</span> : null} <Inline text={q.why} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function GuideBlocks({ blocks, print }: { blocks: GuideBlock[]; print?: boolean }) {
  let qn = 0;
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.t) {
          case 'h2': return <h2 key={i} className="gm-h2"><Inline text={b.text} /></h2>;
          case 'h3': return <h3 key={i} className="gm-h3"><Inline text={b.text} /></h3>;
          case 'p': return <p key={i} className="gm-p"><Inline text={b.text} /></p>;
          case 'ul': return <ul key={i} className="gm-ul">{b.items.map((x, j) => <li key={j}><Inline text={x} /></li>)}</ul>;
          case 'ol': return <ol key={i} className="gm-ol">{b.items.map((x, j) => <li key={j}><Inline text={x} /></li>)}</ol>;
          case 'check': return <ul key={i} className="gm-check">{b.items.map((x, j) => <li key={j}><span className="gm-box" /> <Inline text={x} /></li>)}</ul>;
          case 'quote': return <blockquote key={i} className="gm-quote">{b.lines.map((l, j) => (l.trim() ? <p key={j}><Inline text={l} /></p> : <br key={j} />))}</blockquote>;
          case 'table':
            return (
              <div key={i} className="gm-table-wrap">
                <table className="gm-table">
                  <thead><tr>{b.head.map((h, j) => <th key={j}><Inline text={h} /></th>)}</tr></thead>
                  <tbody>{b.rows.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k}><Inline text={c} /></td>)}</tr>)}</tbody>
                </table>
              </div>
            );
          case 'callout':
            return (
              <div key={i} className={`gm-callout gm-${b.kind}`}>
                <div className="gm-callout-title">{b.title || CALLOUT_LABEL[b.kind] || 'Note'}</div>
                <GuideBlocks blocks={b.blocks} print={print} />
              </div>
            );
          case 'quiz': {
            const start = qn + 1;
            qn += b.items.length;
            return <QuizView key={i} items={b.items} print={print} startNumber={start} />;
          }
          default: return null;
        }
      })}
    </>
  );
}

// One stylesheet for both the in-app reader and the print window.
export const GUIDE_CSS = `
.gm-body{font-family:Georgia,"Times New Roman",serif;color:#1e293b;font-size:17px;line-height:1.7}
.gm-body .gm-h2{font-family:"DM Sans",Inter,system-ui,sans-serif;font-size:1.45em;font-weight:800;color:#0f1b4d;margin:1.8em 0 .5em;line-height:1.25}
.gm-body .gm-h3{font-family:"DM Sans",Inter,system-ui,sans-serif;font-size:1.15em;font-weight:800;color:#0f1b4d;margin:1.4em 0 .4em}
.gm-body .gm-p{margin:.75em 0}
.gm-body .gm-ul,.gm-body .gm-ol{margin:.6em 0 .9em 1.4em}
.gm-body .gm-ul{list-style:disc}.gm-body .gm-ol{list-style:decimal}
.gm-body li{margin:.25em 0}
.gm-body .gm-check{list-style:none;margin:.6em 0;padding:0}
.gm-body .gm-check li{display:flex;gap:.55em;align-items:flex-start}
.gm-body .gm-box{flex:none;width:.95em;height:.95em;border:2px solid #0E9594;border-radius:3px;margin-top:.35em}
.gm-body .gm-quote{border-left:4px solid #0E9594;background:#f0fdfa;margin:1em 0;padding:.7em 1em;border-radius:0 8px 8px 0}
.gm-body .gm-quote p{margin:.3em 0}
.gm-body .gm-table-wrap{overflow-x:auto;margin:1em 0}
.gm-body .gm-table{border-collapse:collapse;width:100%;font-family:"DM Sans",Inter,system-ui,sans-serif;font-size:.85em;line-height:1.45}
.gm-body .gm-table th{background:#0f1b4d;color:#fff;text-align:left;padding:.55em .7em;font-weight:700}
.gm-body .gm-table td{border-bottom:1px solid #e2e8f0;padding:.5em .7em;vertical-align:top}
.gm-body .gm-table tr:nth-child(even) td{background:#f8fafc}
.gm-body .gm-callout{border-radius:12px;padding:.9em 1.1em;margin:1.2em 0;border:1px solid}
.gm-body .gm-callout-title{font-family:"DM Sans",Inter,system-ui,sans-serif;font-weight:800;font-size:.8em;text-transform:uppercase;letter-spacing:.04em;margin-bottom:.3em}
.gm-body .gm-callout .gm-p:first-of-type{margin-top:.2em}
.gm-body .gm-example{background:#eef2ff;border-color:#c7d2fe}.gm-body .gm-example .gm-callout-title{color:#3730a3}
.gm-body .gm-errors{background:#fff7ed;border-color:#fed7aa}.gm-body .gm-errors .gm-callout-title{color:#9a3412}
.gm-body .gm-tip{background:#f0fdfa;border-color:#99f6e4}.gm-body .gm-tip .gm-callout-title{color:#0f766e}
.gm-body .gm-key{background:#0f1b4d;border-color:#0f1b4d;color:#e0e7ff}.gm-body .gm-key .gm-callout-title{color:#5eead4}.gm-body .gm-key strong{color:#fff}
.gm-body .gm-quiz{margin:1.2em 0}
.gm-body .gm-q{border:1px solid #e2e8f0;border-radius:12px;padding:1em 1.1em;margin:.9em 0;background:#fff}
.gm-body .gm-passage{background:#f8fafc;border-radius:8px;padding:.7em .9em;margin-bottom:.7em;font-size:.95em}
.gm-body .gm-passage p{margin:.35em 0}
.gm-body .gm-qtext{margin:.2em 0 .6em}
.gm-body .gm-choices{display:grid;gap:.45em}
.gm-body .gm-choice{display:flex;gap:.6em;align-items:flex-start;text-align:left;border:1px solid #cbd5e1;border-radius:10px;padding:.55em .7em;background:#fff;font:inherit;font-size:.95em;cursor:pointer;color:inherit}
.gm-body .gm-choice:hover:not(:disabled){border-color:#0E9594;background:#f0fdfa}
.gm-body .gm-letter{flex:none;font-family:"DM Sans",Inter,system-ui,sans-serif;font-weight:800;color:#0f1b4d;width:1.4em}
.gm-body .gm-right{border-color:#0E9594;background:#ccfbf1}
.gm-body .gm-wrong{border-color:#f97316;background:#fff7ed}
.gm-body .gm-dim{opacity:.6}
.gm-body .gm-why{margin-top:.7em;padding:.7em .9em;border-radius:10px;font-size:.93em}
.gm-body .gm-why-ok{background:#f0fdfa;border:1px solid #99f6e4}
.gm-body .gm-why-no{background:#fff7ed;border:1px solid #fed7aa}
.gm-body .gm-cat{font-family:"DM Sans",Inter,system-ui,sans-serif;font-size:.8em;font-weight:700;color:#64748b}
.gm-body .gm-opts{margin:.3em 0 0 1.6em;list-style:upper-alpha}
.gm-body .gm-answers{border-top:2px solid #0f1b4d;margin-top:1em;padding-top:.6em;font-size:.9em}
.gm-body .gm-answers p{margin:.35em 0}
.gm-body code{font-family:ui-monospace,Menlo,monospace;font-size:.9em;background:#f1f5f9;padding:.05em .3em;border-radius:4px}
@media (max-width:640px){.gm-body{font-size:16px}}
`;

export const GUIDE_PRINT_CSS = `
@page{size:letter;margin:0.8in 0.75in 0.9in}
html,body{background:#fff;margin:0}
body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
.gp-cover{height:8.4in;display:flex;flex-direction:column;justify-content:space-between;page-break-after:always;font-family:"DM Sans",Inter,system-ui,sans-serif}
.gp-band{background:#0f1b4d;color:#fff;border-radius:14px;padding:.9in .6in}
.gp-brand{color:#5eead4;font-weight:800;letter-spacing:.08em;text-transform:uppercase;font-size:12px}
.gp-title{font-size:34px;font-weight:800;line-height:1.15;margin:.25in 0 .15in}
.gp-sub{font-size:16px;color:#c7d2fe}
.gp-meta{font-size:12px;color:#475569;line-height:1.6}
.gp-toc{page-break-after:always;font-family:"DM Sans",Inter,system-ui,sans-serif}
.gp-toc h2{color:#0f1b4d;font-size:22px}
.gp-toc li{margin:.5em 0;font-size:14px}
.gp-chapter{page-break-before:always}
.gp-chapter:first-of-type{page-break-before:auto}
.gp-chapter-head{font-family:"DM Sans",Inter,system-ui,sans-serif;border-bottom:3px solid #0E9594;padding-bottom:.15in;margin-bottom:.2in}
.gp-chapter-num{color:#0E9594;font-weight:800;font-size:12px;letter-spacing:.08em;text-transform:uppercase}
.gp-chapter-title{color:#0f1b4d;font-size:26px;font-weight:800;margin:.05in 0 0}
.gp-locked{border:1px dashed #94a3b8;border-radius:10px;padding:.2in;color:#475569;font-family:"DM Sans",Inter,system-ui,sans-serif;font-size:13px}
.gp-foot{position:fixed;bottom:-0.55in;left:0;right:0;text-align:center;font:11px "DM Sans",Inter,system-ui,sans-serif;color:#94a3b8}
.gm-body{font-size:11pt;line-height:1.55}
.gm-body .gm-q{padding:.6em .8em;margin:.6em 0}
.gm-body .gm-callout{padding:.6em .9em;margin:.9em 0}
.gm-body .gm-q,.gm-body .gm-callout,.gm-body table,.gm-body .gm-quote{page-break-inside:avoid}
.gm-body .gm-h2,.gm-body .gm-h3{page-break-after:avoid}
.gp-hint{font:13px "DM Sans",Inter,system-ui,sans-serif;background:#f0fdfa;border:1px solid #99f6e4;border-radius:10px;padding:10px 14px;margin:0 0 18px}
@media print{.gp-hint{display:none}}
`;
